import * as Yup from 'yup';
import { Field, Formik } from 'formik';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../../shared/components';
import { FormikUtils } from '../../../shared/utils';
import {
	CompanyContactDataRoom,
	CompanyContactDataRoomAccessTypeMap,
	CurrentUser,
	ParticipantCompany,
} from '../../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../../shared/context';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { useSnackbar } from 'notistack';
import { QueryKeys } from '../../../shared/hooks';
import { getApiResponseData } from '../../../shared/utils/axios';

interface AddColleagueDrawerProps {
	open: boolean;
	isContactParentCompany: boolean;
	companies: ParticipantCompany[];
	companyGuid: string | undefined;
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
	onClose: () => void;
}

export const AddColleagueDrawer: React.FC<AddColleagueDrawerProps> = ({
	open,
	isContactParentCompany,
	companies,
	companyGuid,
	dataRoomGuid,
	currentUser,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();
	const parentCompany = companies.find(c => c.isParentCompany);
	const childCompany = companies.find(c => c.isChildCompany);

	const newUserMutation = useMutation(
		async ({ values }: CompanyContactUserMutationVariables) => {
			const { data, error } = await getApiResponseData<CompanyContactDataRoom>(
				api.post(
					CerebroApiEndpoints.CompanyContacts.AddContactAndDataRoomAccess(values.companyGuid, dataRoomGuid),
					values
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.DataRoomContacts.Root(dataRoomGuid));
				queryClient.invalidateQueries(QueryKeys.DataRoomContacts.BorrowerParticipants(dataRoomGuid));
				snackbar.enqueueSnackbar('Colleague accessed updated.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error, { helpers }) => {
				if (error.message.includes('already exists')) {
					snackbar.enqueueSnackbar(error.message, { variant: 'error' });
				} else {
					helpers.setFieldError('emailAddress', error.message);
				}
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={() => onClose()}>
			<Formik
				enableReinitialize
				validationSchema={AddContactSchema}
				onSubmit={FormikUtils.MutateOnSubmit(newUserMutation.mutate)}
				initialValues={
					{
						companyGuid: companyGuid,
						firstName: '',
						lastName: '',
						emailAddress: '',
						accessStatus: 'FullAccess',
						isBillingContact: true,
						isThirdParty: false,
						sendEmail: true,
					} as CompanyContactUserFormValues
				}
			>
				{({ isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm
						flatIndicator
						onSubmit={handleSubmit}
						sx={{ paddingRight: 3, width: { xs: '100%', md: 400 } }}
					>
						<CerebroHeading size="h2" component="h2" gutterBottom>
							Add a Colleague
						</CerebroHeading>

						{isContactParentCompany && companies.length > 1 && (
							<>
								<CerebroText sx={{ marginBottom: 3 }}>
									Add a colleague at {parentCompany?.companyName} that can be given access to deals at
									any related company, or at {childCompany?.companyName} who can only be given access
									to deals at {childCompany?.companyName}.
								</CerebroText>

								<CerebroLabel htmlFor="companyGuid" sx={{ marginTop: 3 }}>
									Company
								</CerebroLabel>
								<Field id="companyGuid" as={CerebroDropdown} name="companyGuid" disabled={isSubmitting}>
									{companies.map(company => (
										<CerebroDropdownMenuItem key={company.guid} value={company.guid}>
											{company.companyName}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</>
						)}

						<CerebroLabel required htmlFor="title" sx={{ marginTop: 3 }}>
							First Name
						</CerebroLabel>
						<Field
							id="firstName"
							name="firstName"
							as={CerebroInput}
							error={FormikUtils.IsInvalid('firstName', errors, touched)}
							disabled={isSubmitting}
						/>
						{FormikUtils.IsInvalid('firstName', errors, touched) && (
							<CerebroText variant="error">{errors.firstName}</CerebroText>
						)}

						<CerebroLabel required htmlFor="lastName" sx={{ marginTop: 3 }}>
							Last Name
						</CerebroLabel>
						<Field
							id="lastName"
							name="lastName"
							as={CerebroInput}
							error={FormikUtils.IsInvalid('lastName', errors, touched)}
							disabled={isSubmitting}
						/>
						{FormikUtils.IsInvalid('lastName', errors, touched) && (
							<CerebroText variant="error">{errors.lastName}</CerebroText>
						)}

						<CerebroLabel required htmlFor="emailAddress" sx={{ marginTop: 3 }}>
							Email Address
						</CerebroLabel>
						<Field
							id="emailAddress"
							name="emailAddress"
							as={CerebroInput}
							error={FormikUtils.IsInvalid('emailAddress', errors, touched)}
							disabled={isSubmitting}
						/>
						{FormikUtils.IsInvalid('emailAddress', errors, touched) && (
							<CerebroText variant="error">{errors.emailAddress}</CerebroText>
						)}

						<CerebroLabel htmlFor="isThirdParty" sx={{ marginTop: 3 }}>
							3rd Party Contact
						</CerebroLabel>
						<Field id="isThirdParty" name="isThirdParty" as={CerebroYesNo} disabled={isSubmitting} />

						<CerebroText variant="hint">
							<ul>
								<li>
									3rd Party Contacts are users outside your company that require access to your loan
									request.
								</li>
							</ul>
						</CerebroText>

						<CerebroLabel htmlFor="accessStatus" sx={{ marginTop: 3 }}>
							Access Level
						</CerebroLabel>
						<Field id="accessStatus" as={CerebroDropdown} name="accessStatus" disabled={isSubmitting}>
							{Object.entries(CompanyContactDataRoomAccessTypeMap)
								.filter(a => a[0].indexOf('NoAccess'))
								.map(([value, description]) => (
									<CerebroDropdownMenuItem key={value} value={value}>
										{description}
									</CerebroDropdownMenuItem>
								))}
						</Field>

						<CerebroText variant="hint">
							<ul>
								<li>Full Access: can access and edit all sections of the loan application.</li>
								<li>
									Limited Access: can access and edit all sections of the loan application; can not
									give access to others.
								</li>
								<li>No Access: no access to the loan application.</li>
							</ul>
						</CerebroText>

						{currentUser.isEmployeeUser && (
							<>
								<CerebroLabel htmlFor="isBillingContact" sx={{ marginTop: 3 }}>
									Billing Contact
								</CerebroLabel>
								<Field
									id="isBillingContact"
									name="isBillingContact"
									as={CerebroYesNo}
									disabled={isSubmitting}
								/>

								<CerebroLabel htmlFor="sendEmail" sx={{ marginTop: 3 }}>
									Send Email
								</CerebroLabel>
								<Field id="sendEmail" name="sendEmail" as={CerebroYesNo} disabled={isSubmitting} />
							</>
						)}

						<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 6 }}>
							Save Colleague
						</CerebroPrimaryButton>

						{!currentUser.isEmployeeUser && (
							<CerebroText variant="hint" sx={{ marginTop: 2 }}>
								Your colleague will receive an email with instructions for accessing their account.
							</CerebroText>
						)}
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

interface CompanyContactUserFormValues {
	companyGuid: string;
	firstName: string;
	lastName: string;
	emailAddress: string;
	accessStatus: string;
	isBillingContact: boolean;
	isThirdParty: boolean;
	sendEmail: boolean;
}

type CompanyContactUserMutationVariables = FormikUtils.MutateOnSubmitVariables<CompanyContactUserFormValues>;

const AddContactSchema = Yup.object().shape({
	firstName: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
	lastName: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
	emailAddress: Yup.string().email('Invalid email').required('Required'),
});
